<template>
  <div class="notification-system">
    <v-snackbar
      v-for="notification in localNotifications"
      :key="notification.compositeId"
      v-model="notification.show"
      :color="getColor(notification.type)"
      :timeout="notification.duration || 5000"
      :location="(notification.location as any) || 'top'"
      class="notification-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon :icon="getIcon(notification.type)" class="mr-2" />
        {{ notification.message }}
      </div>
      <template v-slot:actions>
        <v-btn
          variant="text"
          color="white"
          data-usage-feature="notification:dismiss"
          @click="removeNotification(notification)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">


import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUIStore } from '@/stores/useUIStore'
import { trackUsageAction } from '@/utils/telemetry'
import type { Notification } from '@/stores/types/ui.types'

type LocalNotification = Notification & {
  show: boolean
  compositeId: string
  location?: string
}

const uiStore = useUIStore()
const { notifications: uiNotifications } = storeToRefs(uiStore)

const localNotifications = ref<LocalNotification[]>([])

const notifications = computed<LocalNotification[]>(() => {
  return (uiNotifications.value || []).map((n: Notification) => ({
    ...n,
    show: true,
    location: 'top',
    compositeId: `pinia-${n.id}`
  }))
})

watch(notifications, (storeNotifications) => {
  const currentLocal = [...localNotifications.value]

  localNotifications.value = storeNotifications.map((notification) => {
    const existing = currentLocal.find(
      (n) => n.compositeId === notification.compositeId
    )
    return {
      ...notification,
      show: existing ? existing.show : true
    }
  })
}, { immediate: true })

function getColor(type: Notification['type']): string {
  switch (type) {
    case 'success': return 'success'
    case 'error': return 'error'
    case 'warning': return 'warning'
    case 'info':
    default: return 'info'
  }
}

function getIcon(type: Notification['type']): string {
  switch (type) {
    case 'success': return 'mdi-check-circle'
    case 'error': return 'mdi-alert-circle'
    case 'warning': return 'mdi-alert'
    case 'info':
    default: return 'mdi-information'
  }
}

function removeNotification(notification: LocalNotification) {
  trackUsageAction('notification:dismiss', 'click')
  const index = localNotifications.value.findIndex(
    (n) => n.compositeId === notification.compositeId
  )
  if (index !== -1) {
    const target = localNotifications.value[index]
    if (target) {
      target.show = false
      setTimeout(() => {
        uiStore.removeNotification(notification.id)
      }, 300)
    }
  }
}
</script>

<style scoped>
.notification-system {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
}

.notification-snackbar {
  pointer-events: auto;
  margin-bottom: 8px;
}
</style>
